import React, { useCallback } from "react";
import styled from "styled-components";
import {
  componentClasses,
  StyledCard,
  StyledDiv,
  StyledTypography,
} from "./GlobalRecommendationsStyles";
import TooltipComponent from "components/common/ToolTip";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlayWithSpinner } from "components/common/LoadingOverlay";
import {
  selectAnalyticsEndDate,
  selectAnalyticsStartDate,
  selectDateRange,
  selectProductAnalyticsData,
  selectIsProductChildrenFetched,
  selectIsProductChildrenFetching,
  selectCategoryByProductId,
} from "store/product/ProductSelectors";
import {
  analyticsDataLoader,
  loadSingleProductAnalytics,
  resetSingleProducAnalyticView,
  loadCategoryByProductId,
} from "store/product/ProductActions";
import AppState from "store/AppState";
import AnalyticsOverlay from "components/overlays/AnalyticsOverlay";
import ProductCategoryOverlay from "components/overlays/ProductCategoryOverlay";
import { FreqBoughtProductData } from "store/recommendations/RecommendationType";
import ProductActionIcons from "components/common/ProductActionIcons";
import { selectIsAnalyticsEnabled } from "store/adapter-config/AdapterConfigSelectors";
import { useProductImage } from "hooks/useProductImage";
import { refreshProductFromList } from "store/product-list/ProductListActions";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";

const ImageContainerNonBase = styled.div`
  display: flex;
  justify-content: center; /* Horizontally centers the container */
  align-items: center;   
  overflow: hidden;
  margin: auto;
  width: 170px;
  height: 230px;
  padding-top: 4px;
  & .image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  }
`;

interface Props {
  product?: FreqBoughtProductData;
  code: string;
  productId: string;
  baseProductId?: string;
  height?: string;
  isColumn?: boolean;
  price?: Number;
  index?: Number;
  onTileClick?: (productId: string | null) => void;
  isAnalyticsDisplayed?: boolean;
  setIsAnalyticsDisplayed?: (value: boolean) => void;
  isCategoriesDisplayed?: boolean;
  setIsCategoriesDisplayed?: (value: boolean) => void;
}

const GlobalRecommendationProductItem: React.FC<Props> = (props) => {
  const {
    product,
    code,
    productId,
    isColumn,
    index,
    price,
    baseProductId,
    isAnalyticsDisplayed,
    setIsAnalyticsDisplayed,
    isCategoriesDisplayed,
    setIsCategoriesDisplayed,
    ...otherProps
  } = props;
  const currencySymbol = useCurrencySymbol(product?.currencyCode);
  const dispatch = useDispatch();
  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const selectedDateRange = useSelector(selectDateRange);
  const catalogId = useSelector(selectCurrentCatalogId);
  const storeId = useSelector(selectCurrentStoreId);
  const localeCode = useSelector(selectCurrentLocale);
  const isAnalyticsEnabled = useSelector((state: AppState) =>
    selectIsAnalyticsEnabled(state),
  );
  const productAnalyticsId = product?.analyticsId ?? code;
  const showProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.showAnalytics;
  const loadSingleProduct = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.loadAnalytics;
  const isRefreshing = product?.isRefreshing || false;
  const isProductChildrenFetched = useSelector(selectIsProductChildrenFetched);
  const isProductChildrenFetching = useSelector(
    selectIsProductChildrenFetching,
  );
  const categoryList = useSelector((state: AppState) =>
    selectCategoryByProductId(state, productId),
  );
  const hanldeProductAnalytics = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (isCategoriesDisplayed) {
      setIsCategoriesDisplayed?.(false);
    }
    if (!showProductAnalytics) {
      setIsAnalyticsDisplayed?.(true);
      dispatch(analyticsDataLoader(productAnalyticsId));
      const fromDate = selectedDateRange.startDate;
      const toDate = selectedDateRange.endDate;
      if (fromDate !== startDate || toDate !== endDate) {
        const analyticsPayload = {
          startDate: fromDate,
          endDate: toDate,
          productCodes: [productAnalyticsId],
        };
        dispatch(loadSingleProductAnalytics(analyticsPayload));
      }
    } else {
      setIsAnalyticsDisplayed?.(false);
      dispatch(resetSingleProducAnalyticView(productAnalyticsId));
    }
  };

  const handleRefreshProduct = useCallback(
    (event) => {
      event.stopPropagation();
      let payload = {
        productId,
        catalogId,
        localeCode,
        storeId,
      };
      dispatch(refreshProductFromList(payload));
    },
    [catalogId, localeCode, storeId, dispatch, productId],
  );

  const productImage = useProductImage(productId, product);

  const renderProductImage = (
    isColumn,
    productImage,
    product,
    componentClasses,
  ) => {
    if (isColumn) {
      return (
        <ImageContainerNonBase>
          {productImage ? (
            <img
              className="image"
              src={productImage}
              alt={product?.name}
              draggable={false}
            />
          ) : (
            <StyledDiv
              className={componentClasses.emptyImageContainer}
            ></StyledDiv>
          )}
        </ImageContainerNonBase>
      );
    }
  };

  const renderProductInfo = (isColumn, product, price, componentClasses) => {
    const calculatePrice = isColumn
      ? price
        ? `${currencySymbol}${price}`
        : ""
      : product?.price
        ? `${currencySymbol}${product?.price}`
        : "";
    return (
      <StyledDiv
        className={componentClasses.imageProductCodeDiv}
        isColumn={isColumn}
      >
        <TooltipComponent tooltipTitle={product?.name || ""} placement="bottom">
          <StyledTypography
            className={componentClasses.productName}
            isColumn={isColumn}
          >
            {product?.name || ""}
          </StyledTypography>
        </TooltipComponent>
        <TooltipComponent tooltipTitle={`${product?.code}`} placement="bottom">
          <StyledTypography
            className={componentClasses.productCodes}
            isColumn={isColumn}
          >
            {`${product?.code}`}
          </StyledTypography>
        </TooltipComponent>
        <TooltipComponent tooltipTitle={calculatePrice} placement="bottom">
          <StyledTypography
            className={componentClasses.productPrice}
            isColumn={isColumn}
          >
            {calculatePrice}
          </StyledTypography>
        </TooltipComponent>
      </StyledDiv>
    );
  };

  const handleCategories = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    if (!isCategoriesDisplayed) {
      if (showProductAnalytics) {
        setIsAnalyticsDisplayed?.(false);
        dispatch(resetSingleProducAnalyticView(productAnalyticsId));
      }
      setIsCategoriesDisplayed?.(true);
      const payload = {
        productIds: [productId],
      };
      dispatch(loadCategoryByProductId(payload));
    } else {
      setIsCategoriesDisplayed?.(false);
    }
  };

  return (
    <StyledDiv>
      <StyledDiv className={componentClasses.productCardContainer}>
        {isRefreshing && <LoadingOverlayWithSpinner />}
        {isColumn && (loadSingleProduct || showProductAnalytics) && (
          <AnalyticsOverlay
            productId={productId}
            showProductAnalytics={showProductAnalytics}
            loadSingleProductAnalytics={loadSingleProduct}
            isSubstituteOverlay={true}
            isRecommendationView={true}
          />
        )}
        {isColumn &&
          isCategoriesDisplayed &&
          (isProductChildrenFetching ||
            isProductChildrenFetched ||
            categoryList.length > 0) && (
            <ProductCategoryOverlay
              productId={productId}
              showOverlay={true}
              isSubstituteOverlay={true}
            />
          )}
        <StyledCard
          variant="outlined"
          className={
            !isColumn
              ? componentClasses.baseProductCard
              : componentClasses.productCard
          }
          {...otherProps}
        >
          <StyledDiv className={componentClasses.cardContentDiv}>
            <StyledTypography
              className={componentClasses.productType}
              isColumn={isColumn}
            >
              {product?.typeCode ?? "Product"}
            </StyledTypography>
            <StyledTypography className={componentClasses.stockBase}>
              INV: {product?.stock ?? 0}
              {isColumn && (
                <ProductActionIcons
                  product={product}
                  isAnalyticsEnabled={!!isAnalyticsEnabled}
                  onRefresh={handleRefreshProduct}
                  onAnalytics={hanldeProductAnalytics}
                  onCategories={handleCategories}
                />
              )}
            </StyledTypography>
            {renderProductImage(
              isColumn,
              productImage,
              product,
              componentClasses,
            )}

            {renderProductInfo(isColumn, product, price, componentClasses)}
          </StyledDiv>
        </StyledCard>
      </StyledDiv>
    </StyledDiv>
  );
};

export default GlobalRecommendationProductItem;
